// src/components/opportunity/OpportunityPricingFields.tsx
//
// Small presentational helpers carved out of `OpportunityPricingTab.tsx`
// to keep that file under the file-size hygiene cap. These have zero
// state of their own — every value/handler flows from the parent.

import type { OpportunityPricingTotals } from "@/types/opportunity";
import { formatOpportunityCurrency, formatOpportunityPct } from "@/lib/opportunity/formatCurrency";

export function MoneyField(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="text-xs font-medium text-gray-600 dark:text-zinc-300">{props.label}</label>
      <input
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder="0.00"
        inputMode="decimal"
        className="mt-1 w-full text-sm border border-gray-200 rounded-lg px-3 py-2 tabular-nums dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-100"
      />
    </div>
  );
}

export function PctField(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  defaultPct: number | null;
  delta: number | null;
}) {
  return (
    <div>
      <label className="text-xs font-medium text-gray-600 dark:text-zinc-300">{props.label}</label>
      <input
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder="0"
        inputMode="decimal"
        className={
          "mt-1 w-full text-sm border rounded-lg px-3 py-2 tabular-nums dark:bg-zinc-800 dark:text-zinc-100 " +
          (props.delta != null
            ? "border-amber-300 dark:border-amber-500"
            : "border-gray-200 dark:border-zinc-700")
        }
      />
      {props.defaultPct != null ? (
        <div className="text-[11px] mt-1 text-gray-500 dark:text-zinc-400">
          Org default: {formatOpportunityPct(props.defaultPct)}
          {props.delta != null ? (
            <span className="ml-1 text-amber-700 dark:text-amber-300">
              · edited ({props.delta > 0 ? "+" : ""}
              {props.delta}%)
            </span>
          ) : null}
        </div>
      ) : (
        <div className="text-[11px] mt-1 text-gray-400 dark:text-zinc-500">
          No org default set
        </div>
      )}
    </div>
  );
}

export function TotalsCard(props: {
  totals: OpportunityPricingTotals;
  commissionPct: number;
  marginPct: number;
  depositPct: number;
}) {
  const { totals } = props;
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
      <h4 className="text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-400 mb-2">
        Live totals
      </h4>
      <dl className="space-y-1.5 text-sm">
        <TotalsRow label="Total cost" value={formatOpportunityCurrency(totals.total_cost)} />
        <TotalsRow
          label={`Commission (${formatOpportunityPct(props.commissionPct)})`}
          value={formatOpportunityCurrency(totals.commission_amount)}
        />
        <TotalsRow
          label={`Margin (${formatOpportunityPct(props.marginPct)})`}
          value={formatOpportunityCurrency(totals.margin_amount)}
        />
        <div className="border-t border-gray-100 dark:border-zinc-700 pt-2 mt-2" />
        <TotalsRow
          label="Sale price"
          value={formatOpportunityCurrency(totals.total_sale_price)}
          emphasis
        />
        <TotalsRow
          label={`Deposit (${formatOpportunityPct(props.depositPct)})`}
          value={formatOpportunityCurrency(totals.deposit_amount)}
        />
        <TotalsRow
          label="Balance on completion"
          value={formatOpportunityCurrency(totals.remaining_balance)}
        />
      </dl>
    </div>
  );
}

function TotalsRow(props: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs text-gray-600 dark:text-zinc-400">{props.label}</dt>
      <dd
        className={
          "tabular-nums " +
          (props.emphasis
            ? "text-base font-semibold text-gray-900 dark:text-zinc-100"
            : "text-gray-900 dark:text-zinc-100")
        }
      >
        {props.value}
      </dd>
    </div>
  );
}

/**
 * Returns the signed delta between a user-entered pct and the org
 * default, rounded to 2 decimals. Null when the input is empty, the
 * default is missing, or the delta is < ~0.005 (i.e. effectively equal).
 */
export function pctDelta(input: string, defaultPct: number | null | undefined): number | null {
  if (defaultPct == null || !Number.isFinite(defaultPct)) return null;
  const n = Number(input);
  if (!Number.isFinite(n) || input === "") return null;
  const delta = n - defaultPct;
  if (Math.abs(delta) < 0.005) return null;
  return Math.round(delta * 100) / 100;
}
